import { Editor } from "@monaco-editor/react"
import useTranslate from "@hooks/use_translate"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { showSuccessToast } from "@utils/toast"
import { useMemo, useState } from "react"

import { formatBlock } from "./operatorRun.utils"

const LINE_HEIGHT = 19
const PADDING = 8

type SizedEditor = {
  getContentHeight: () => number
  onDidContentSizeChange: (listener: () => void) => void
}

type Props = {
  label?: string
  value: unknown
  truncated?: boolean
  maxHeight?: number
  language?: "markdown"
}

export default function JsonBox({
  label,
  value,
  truncated,
  maxHeight = 360,
  language
}: Props) {
  const { translate } = useTranslate()
  const block = useMemo(() => formatBlock(value), [value])
  const [height, setHeight] = useState(() =>
    Math.min(
      block.text.split("\n").length * LINE_HEIGHT + PADDING * 2,
      maxHeight
    )
  )

  const copy = () => {
    void navigator.clipboard
      .writeText(block.text)
      .then(() => showSuccessToast(translate("ai_operator_copied")))
  }

  return (
    <Box>
      {label || truncated ? (
        <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
          {label ? (
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
            >
              {label}
            </Typography>
          ) : null}
          {truncated ? (
            <Typography variant="caption" color="warning.dark">
              {translate("ai_operator_truncated")}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
      <Box
        sx={{
          position: "relative",
          border: 1,
          borderColor: "divider",
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: "background.paper"
        }}
      >
        <Editor
          height={height}
          language={language ?? block.language}
          value={block.text}
          onMount={(editor: SizedEditor) => {
            const fit = () =>
              setHeight(Math.min(editor.getContentHeight(), maxHeight))
            editor.onDidContentSizeChange(fit)
            fit()
          }}
          options={{
            readOnly: true,
            domReadOnly: true,
            minimap: { enabled: false },
            fontSize: 12.5,
            lineHeight: LINE_HEIGHT,
            wordWrap: "on",
            wrappingIndent: "indent",
            scrollBeyondLastLine: false,
            renderLineHighlight: "none",
            lineNumbersMinChars: 3,
            overviewRulerLanes: 0,
            hideCursorInOverviewRuler: true,
            contextmenu: false,
            unicodeHighlight: {
              ambiguousCharacters: false,
              invisibleCharacters: false,
              nonBasicASCII: false
            },
            padding: { top: PADDING, bottom: PADDING },
            scrollbar: { alwaysConsumeMouseWheel: false }
          }}
        />
        <Tooltip title={translate("ai_operator_copy")}>
          <IconButton
            size="small"
            onClick={copy}
            sx={{
              position: "absolute",
              top: 4,
              right: 16,
              bgcolor: "background.paper",
              "&:hover": { bgcolor: "action.hover" }
            }}
          >
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}
